import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { API_BASE as API } from '../lib/config';
import toast from 'react-hot-toast';
import {
  ChevronLeftIcon,
  PaperAirplaneIcon,
  PaperClipIcon,
  DocumentIcon,
  PhoneIcon,
  VideoCameraIcon
} from '@heroicons/react/24/outline';

// Use unified API base configured in lib/config

const ChatRoomPage = () => {
  const { roomId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);
  
  const [chatRoom, setChatRoom] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (roomId) {
      fetchChatRoom();
      fetchMessages();
    }
  }, [roomId]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const fetchChatRoom = async () => {
    try {
      const headers = {
        'Authorization': `Bearer ${localStorage.getItem('token')}`
      };
      
      const response = await axios.get(`${API}/chat-rooms`, { headers });
      const room = response.data.find(r => r.id === roomId);
      setChatRoom(room);
    } catch (error) {
      console.error('Failed to fetch chat room:', error);
      toast.error('チャットルームの取得に失敗しました');
    }
  };

  const fetchMessages = async () => {
    setLoading(true);
    try {
      const headers = {
        'Authorization': `Bearer ${localStorage.getItem('token')}`
      };
      
      const response = await axios.get(`${API}/chat-rooms/${roomId}/messages`, { headers });
      setMessages(response.data);
    } catch (error) {
      console.error('Failed to fetch messages:', error);
      toast.error('メッセージの取得に失敗しました');
    } finally {
      setLoading(false);
    }
  };

  const sendMessage = async () => {
    if (!newMessage.trim() || sending) return;

    setSending(true);
    try {
      const headers = {
        'Authorization': `Bearer ${localStorage.getItem('token')}`
      };

      const messageData = {
        message_type: 'text',
        content: newMessage.trim()
      };

      const response = await axios.post(
        `${API}/chat-rooms/${roomId}/messages`,
        messageData,
        { headers }
      );

      setMessages(prev => [...prev, response.data]);
      setNewMessage('');
    } catch (error) {
      console.error('Failed to send message:', error);
      toast.error('メッセージの送信に失敗しました');
    } finally {
      setSending(false);
    }
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (file.size > 10 * 1024 * 1024) {
      toast.error('ファイルサイズは10MB以下にしてください');
      return;
    }

    setSending(true);
    try {
      const reader = new FileReader();
      reader.onload = async (e) => {
        const headers = {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        };

        const messageData = {
          message_type: file.type.startsWith('image/') ? 'image' : 'file',
          content: file.name,
          file_url: e.target.result,
          file_name: file.name
        };

        const response = await axios.post(
          `${API}/chat-rooms/${roomId}/messages`,
          messageData,
          { headers }
        );

        setMessages(prev => [...prev, response.data]);
        setSending(false);
      };
      reader.readAsDataURL(file);
    } catch (error) {
      console.error('Failed to upload file:', error);
      toast.error('ファイルのアップロードに失敗しました');
      setSending(false);
    }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const formatMessageTime = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffDays = Math.floor((now - date) / (1000 * 60 * 60 * 24));

    if (diffDays === 0) {
      return date.toLocaleTimeString('ja-JP', { hour: '2-digit', minute: '2-digit' });
    } else if (diffDays === 1) {
      return '昨日 ' + date.toLocaleTimeString('ja-JP', { hour: '2-digit', minute: '2-digit' });
    } else {
      return date.toLocaleDateString('ja-JP', { month: 'short', day: 'numeric' }) + ' ' +
             date.toLocaleTimeString('ja-JP', { hour: '2-digit', minute: '2-digit' });
    }
  };

  const renderMessage = (message) => {
    const isMyMessage = message.sender_id === user.id;
    
    return (
      <div key={message.id} className={`flex ${isMyMessage ? 'justify-end' : 'justify-start'} mb-4`}>
        <div className={`max-w-xs lg:max-w-md px-4 py-2 rounded-lg ${
          isMyMessage 
            ? 'bg-blue-600 text-white' 
            : 'bg-gray-200 text-gray-900'
        }`}>
          {!isMyMessage && (
            <p className="text-xs text-gray-500 mb-1">{message.sender_name}</p>
          )}
          
          {message.message_type === 'text' && (
            <p className="text-sm">{message.content}</p>
          )}
          
          {message.message_type === 'image' && (
            <div className="space-y-2">
              <img
                src={message.file_url}
                alt={message.file_name}
                className="max-w-full rounded cursor-pointer"
                onClick={() => window.open(message.file_url, '_blank')}
              />
              <p className="text-xs">{message.file_name}</p>
            </div>
          )}
          
          {message.message_type === 'file' && (
            <div className="flex items-center space-x-2">
              <DocumentIcon className="h-5 w-5" />
              <a
                href={message.file_url}
                download={message.file_name}
                className="text-sm hover:underline"
              >
                {message.file_name}
              </a>
            </div>
          )}
          
          <p className={`text-xs mt-1 ${isMyMessage ? 'text-blue-100' : 'text-gray-500'}`}>
            {formatMessageTime(message.created_at)}
          </p>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">読み込み中...</p>
        </div>
      </div>
    );
  }

  if (!chatRoom) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-600">チャットルームが見つかりません</p>
          <button
            onClick={() => navigate('/chat')}
            className="mt-4 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
          >
            チャット一覧に戻る
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-4 py-4 flex items-center">
        <button
          onClick={() => navigate('/chat')}
          className="mr-4 p-2 hover:bg-gray-100 rounded-full"
        >
          <ChevronLeftIcon className="h-6 w-6 text-gray-600" />
        </button>
        
        <div className="flex-1">
          <h1 className="text-lg font-semibold text-gray-900">
            {chatRoom.job_title}
          </h1>
          <p className="text-sm text-gray-600">
            {user.id === chatRoom.employer_id ? chatRoom.job_seeker_name : chatRoom.employer_name}
          </p>
        </div>

        <div className="flex space-x-2">
          <button className="p-2 hover:bg-gray-100 rounded-full">
            <PhoneIcon className="h-6 w-6 text-gray-600" />
          </button>
          <button className="p-2 hover:bg-gray-100 rounded-full">
            <VideoCameraIcon className="h-6 w-6 text-gray-600" />
          </button>
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {messages.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-gray-500">まだメッセージがありません</p>
            <p className="text-gray-400 text-sm mt-2">最初のメッセージを送信してみましょう！</p>
          </div>
        ) : (
          messages.map(renderMessage)
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Message Input */}
      <div className="bg-white border-t border-gray-200 p-4">
        <div className="flex items-center space-x-2">
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleFileUpload}
            accept="image/*,.pdf,.doc,.docx"
            className="hidden"
          />
          
          <button
            onClick={() => fileInputRef.current?.click()}
            disabled={sending}
            className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-full disabled:opacity-50"
          >
            <PaperClipIcon className="h-6 w-6" />
          </button>

          <div className="flex-1 relative">
            <input
              type="text"
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && sendMessage()}
              placeholder="メッセージを入力..."
              disabled={sending}
              className="w-full px-4 py-2 border border-gray-300 rounded-full focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:opacity-50"
            />
          </div>

          <button
            onClick={sendMessage}
            disabled={!newMessage.trim() || sending}
            className="p-2 bg-blue-600 text-white rounded-full hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <PaperAirplaneIcon className="h-6 w-6" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default ChatRoomPage;